import countries from "i18n-iso-countries";
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
import type { RoomOffer } from "../model/roomOffer";
import type { HotelImage } from "../types/hotelImage";
import type { HotelImageUrl } from "../types/hotelImageUrl";
import { MODAL_IMAGE_LARGE_SIZE } from "@/app/consts/common";
import { useTranslations } from "next-intl";

export const getRooms = (
  roomOffers: RoomOffer[] | null
): RoomOffer[][] | null => {
  if (!roomOffers) {
    return null;
  }
  const providerRoomIds = getCurrentProviderRoomIds(roomOffers);
  const groupedRooms = getGroupedRoomsByProviderRoomIds(
    providerRoomIds,
    roomOffers
  );
  return groupedRooms;
};

const getCurrentProviderRoomIds = (roomOffers: RoomOffer[]): string[] => {
  const providerRoomIds: string[] = [];

  roomOffers.forEach((offer) => {
    if (
      offer.providerRoomId &&
      !providerRoomIds.includes(offer.providerRoomId)
    ) {
      providerRoomIds.push(offer.providerRoomId);
    }
  });
  return providerRoomIds;
};

const getGroupedRoomsByProviderRoomIds = (
  providerRoomIds: string[],
  roomOffers: RoomOffer[]
) => {
  let groupedRoomsByProviderRoomId: Array<RoomOffer[]> = [];
  providerRoomIds.forEach((providerRoomId) => {
    const groupedRoomArray = roomOffers
      ?.filter((roomOffer) => roomOffer.providerRoomId === providerRoomId)
      .sort((roomOfferA, roomOfferB) => {
        const totalPriceA = roomOfferA.priceDetails?.total ?? 0;
        const totalPriceB = roomOfferB.priceDetails?.total ?? 0;
        return totalPriceA - totalPriceB;
      });

    if (groupedRoomArray && groupedRoomArray.length > 0) {
      groupedRoomsByProviderRoomId.push(groupedRoomArray);
    }
  });
  groupedRoomsByProviderRoomId = groupedRoomsByProviderRoomId.sort((a, b) => {
    return a[0]?.priceDetails?.total! - b[0]?.priceDetails?.total!;
  });
  return groupedRoomsByProviderRoomId;
};

export const primaryImagePathSelector = (
  hotelImages: HotelImage[] | undefined
): string => {
  const PLACEHOLDER = "/assets/v2/images/missing-image.png";

  if (!hotelImages || hotelImages.length === 0) {
    return PLACEHOLDER;
  }

  // try to find the primary image that has at least one URL, or default to the first image with also at least one URL
  const primaryImage = hotelImages.find(
    (image) => image.isPrimaryImage && image.urls.length > 0
  ) ||
    hotelImages.find((image) => image.urls.length > 0) || {
      urls: [{ url: PLACEHOLDER }],
    };

  let imagePath = PLACEHOLDER;
  if (primaryImage.urls.length > 0) {
    const maxwidthImage = primaryImage.urls.find(
      (urlObj) => urlObj.maxWidth && urlObj.maxWidth >= MODAL_IMAGE_LARGE_SIZE
    );
    imagePath = maxwidthImage ? maxwidthImage.url : primaryImage.urls[0].url;
  }

  return imagePath;
};

export function secondaryImagePathSelector(urls: HotelImageUrl[]) {
  const image = urls?.find((img) => img.maxWidth && img.maxWidth >= 400);
  return image ? image.url : urls[0].url;
}

export const calculateNights = (from: string, to: string): number => {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const renderGuestsInfo = (
  t: ReturnType<typeof useTranslations>,
  numberOfAdults: number
): string => {
  return `${numberOfAdults ? numberOfAdults + ` ${t("Other.adults")},` : ""} `;
};

export const filterImagesByUniqueCategoryAndSize = (images: HotelImage[]) => {
  const filteredImages =
    images?.reduce((acc: string[], imgObj) => {
      const selectedImage = imgObj.urls.find(
        (urlObj: HotelImageUrl) =>
          urlObj.maxWidth &&
          urlObj.maxWidth !== null &&
          urlObj.maxWidth >= MODAL_IMAGE_LARGE_SIZE
      );

      // If a valid image is found, push its URL
      if (selectedImage) {
        acc.push(selectedImage.url);
      } else {
        if (imgObj.urls.length > 0) {
          acc.push(imgObj.urls[imgObj.urls.length - 1].url);
        }
      }

      return acc;
    }, [] as string[]) || [];

  return filteredImages;
};

export const getCountryName = (name: string) => {
  const countryName = countries.getName(name, "en");
  return countryName;
};
